// @ts-nocheck — Pre-existing type errors (stale code drifted from refactored types / WIP).
// Suppressed to unblock the admin build; runtime unchanged. TODO(owner): restore type-safety.
/**
 * Collections Page
 *
 * Lists all collections and provides management actions.
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Star,
  Image as ImageIcon,
  ArrowLeft,
} from 'lucide-react'
import { useCollections, useDeleteCollection, useUpdateCollection } from '../hooks'
import type { Collection } from '../types'
import { cn } from '@/lib/utils'

// =============================================================================
// Collection Card Component
// =============================================================================

interface CollectionCardProps {
  collection: Collection
  onEdit: (id: number) => void
  onDelete: (id: number) => void
  onToggleVisibility: (id: number, visible: boolean) => void
  onToggleFeatured: (id: number, featured: boolean) => void
}

function CollectionCard({
  collection,
  onEdit,
  onDelete,
  onToggleVisibility,
  onToggleFeatured,
}: CollectionCardProps) {
  return (
    <Card className="group overflow-hidden">
      {/* Cover Image */}
      <div className="aspect-video bg-muted relative">
        {collection.featuredImage ? (
          <img
            src={collection.featuredImage}
            alt={collection.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}
        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-2">
          {collection.featured && (
            <Badge variant="default" className="bg-yellow-500">
              <Star className="h-3 w-3 mr-1" />
              Featured
            </Badge>
          )}
          {!collection.visible && (
            <Badge variant="secondary">
              <EyeOff className="h-3 w-3 mr-1" />
              Hidden
            </Badge>
          )}
        </div>
        {/* Actions */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(collection.id)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleVisibility(collection.id, !collection.visible)}>
                {collection.visible ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleFeatured(collection.id, !collection.featured)}>
                <Star className={cn('h-4 w-4 mr-2', collection.featured && 'fill-current')} />
                {collection.featured ? 'Unfeature' : 'Feature'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => onDelete(collection.id)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {/* Content */}
      <CardContent className="p-4">
        <Link to={`/app/easel/collections/${collection.id}`}>
          <h3 className="font-semibold hover:underline">{collection.title}</h3>
        </Link>
        {collection.subtitle && (
          <p className="text-sm text-muted-foreground mt-1">{collection.subtitle}</p>
        )}
        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <ImageIcon className="h-4 w-4" />
            {collection.artworkCount} {collection.artworkCount === 1 ? 'artwork' : 'artworks'}
          </span>
        </div>
        {collection.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {collection.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {collection.tags.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{collection.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Loading Skeleton
// =============================================================================

function CollectionSkeleton() {
  return (
    <Card className="overflow-hidden">
      <Skeleton className="aspect-video" />
      <CardContent className="p-4 space-y-3">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-1/4" />
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

export function CollectionsPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useCollections()
  const deleteCollection = useDeleteCollection()
  const updateCollection = useUpdateCollection()
  const [deleteId, setDeleteId] = useState<number | null>(null)

  const handleEdit = (id: number) => {
    navigate(`/app/easel/collections/${id}`)
  }

  const handleDelete = async () => {
    if (deleteId) {
      await deleteCollection.mutateAsync(deleteId)
      setDeleteId(null)
    }
  }

  const handleToggleVisibility = async (id: number, visible: boolean) => {
    await updateCollection.mutateAsync({ id, data: { visible } })
  }

  const handleToggleFeatured = async (id: number, featured: boolean) => {
    await updateCollection.mutateAsync({ id, data: { featured } })
  }

  const collections = data?.data || []

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Collections</h1>
            <p className="text-muted-foreground">
              Organize your artworks into curated collections
            </p>
          </div>
        </div>
        <Button onClick={() => navigate('/app/easel/collections/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Collection
        </Button>
      </div>

      {/* Collections Grid */}
      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <CollectionSkeleton key={i} />
          ))}
        </div>
      ) : collections.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <ImageIcon className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">No collections yet</h3>
          <p className="text-muted-foreground mb-4">
            Create your first collection to organize and showcase your artworks
          </p>
          <Button onClick={() => navigate('/app/easel/collections/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Create Collection
          </Button>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {collections.map((collection) => (
            <CollectionCard
              key={collection.id}
              collection={collection}
              onEdit={handleEdit}
              onDelete={setDeleteId}
              onToggleVisibility={handleToggleVisibility}
              onToggleFeatured={handleToggleFeatured}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Collection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this collection? This action cannot be undone.
              The artworks in this collection will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
